import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Calendar, User, MessageSquare, Lock, Eye, EyeOff, ArrowLeft, Inbox, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password from environment variable (fallback for dev)
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'MS@2026admin';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchSubmissions();
    } else {
      setError('Incorrect password');
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Admin Login | Manikandan Sivasankar</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
              <p className="text-gray-500 mt-2">Enter password to view contact submissions</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                data-testid="admin-login-btn"
              >
                Access Dashboard
              </button>
            </form>

            <Link
              to="/"
              className="flex items-center justify-center gap-2 mt-6 text-gray-500 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Website
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Admin Dashboard
  return (
    <>
      <Helmet>
        <title>Contact Submissions | Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contact Submissions</h1>
              <p className="text-gray-500 text-sm">{submissions.length} total messages</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchSubmissions}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Site
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl shadow-sm">
              <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700">No submissions yet</h2>
              <p className="text-gray-500 mt-2">Contact form submissions will appear here</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Submissions List */}
              <div className="lg:col-span-1 space-y-3">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    onClick={() => setSelectedSubmission(submission)}
                    className={`bg-white rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedSubmission?.id === submission.id
                        ? 'ring-2 ring-blue-500 shadow-md'
                        : 'shadow-sm'
                    }`}
                    data-testid={`submission-${submission.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{submission.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{submission.subject}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        submission.status === 'new' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {submission.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {formatDate(submission.submitted_at)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Submission Detail */}
              <div className="lg:col-span-2">
                {selectedSubmission ? (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="border-b border-gray-100 pb-4 mb-6">
                      <h2 className="text-xl font-bold text-gray-900">{selectedSubmission.subject}</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Received on {formatDate(selectedSubmission.submitted_at)}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">From</p>
                          <p className="font-medium text-gray-900">{selectedSubmission.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <a 
                            href={`mailto:${selectedSubmission.email}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {selectedSubmission.email}
                          </a>
                        </div>
                      </div>

                      <div className="mt-6">
                        <div className="flex items-center gap-2 mb-3">
                          <MessageSquare className="w-5 h-5 text-gray-400" />
                          <p className="text-sm font-medium text-gray-500">Message</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-700 whitespace-pre-wrap">{selectedSubmission.message}</p>
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-3">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedSubmission.email).then(() => {
                              setCopiedField('email');
                              setTimeout(() => setCopiedField(null), 2000);
                            });
                          }}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                          data-testid="copy-email-btn"
                        >
                          <Mail size={18} />
                          {copiedField === 'email' ? 'Email Copied!' : 'Copy Email'}
                        </button>
                        <button
                          onClick={() => {
                            const text = `Subject: Re: ${selectedSubmission.subject}\n\n---Original Message---\nFrom: ${selectedSubmission.name}\nEmail: ${selectedSubmission.email}\nSubject: ${selectedSubmission.subject}\n\n${selectedSubmission.message}`;
                            navigator.clipboard.writeText(text).then(() => {
                              setCopiedField('message');
                              setTimeout(() => setCopiedField(null), 2000);
                            });
                          }}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                          data-testid="copy-message-btn"
                        >
                          <MessageSquare size={18} />
                          {copiedField === 'message' ? 'Message Copied!' : 'Copy Message'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-6 text-center py-20">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Select a submission to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminPage;
